/**
 * 電話番号フォーマット関連のユーティリティ関数
 */

/**
 * 電話番号を xxx-xxxx-xxxx 形式にフォーマットする
 * @param phone 電話番号文字列
 * @returns フォーマット済みの電話番号
 */
export const formatPhoneNumber = (phone: string | null | undefined): string => {
  if (!phone) return '';
  
  // 数字以外を除去
  const numbers = phone.replace(/[^\d]/g, '');
  
  // 11桁の場合のみフォーマット
  if (numbers.length === 11) {
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7)}`;
  } else if (numbers.length === 10) {
    // 10桁の場合（固定電話など）
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6)}`;
  }
  
  // その他の場合はそのまま返す
  return phone;
};

/**
 * 入力中の電話番号を自動フォーマットする
 * @param value 入力値
 * @param previousValue 前回の値
 * @returns フォーマット済みの値
 */
export const formatPhoneNumberOnInput = (value: string, previousValue: string = ''): string => {
  // 数字のみを抽出
  const currentNumbers = value.replace(/[^\d]/g, '');
  const previousNumbers = previousValue.replace(/[^\d]/g, '');
  
  // 削除操作の場合
  if (currentNumbers.length < previousNumbers.length) {
    // ハイフンの位置を考慮して削除
    if (value.endsWith('-')) {
      return value.slice(0, -1);
    }
    return formatPhoneDisplay(currentNumbers);
  }
  
  // 追加操作の場合
  return formatPhoneDisplay(currentNumbers);
};

/**
 * 数字文字列を電話番号形式で表示する
 * @param numbers 数字のみの文字列
 * @returns 表示用フォーマット
 */
const formatPhoneDisplay = (numbers: string): string => {
  if (numbers.length <= 3) {
    return numbers;
  } else if (numbers.length <= 7) {
    return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
  } else if (numbers.length <= 11) {
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7)}`;
  }
  // 11桁を超える場合は11桁で切り捨て
  return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
};

/**
 * 電話番号のバリデーション
 * @param phone 電話番号
 * @returns エラーメッセージ（エラーがない場合は空文字）
 */
export const validatePhoneNumber = (phone: string | undefined): string => {
  if (!phone) return ''; // 任意フィールドなので空の場合はOK
  
  const numbers = phone.replace(/[^\d]/g, '');
  
  if (numbers.length === 0) {
    return ''; // 空の場合はOK（任意フィールド）
  }
  
  if (numbers.length !== 10 && numbers.length !== 11) {
    return '電話番号は10桁または11桁で入力してください';
  }
  
  if (!/^0/.test(numbers)) {
    return '電話番号は0から始まる必要があります';
  }
  
  return '';
};

/**
 * 電話番号から数字のみを抽出
 * @param phone フォーマット済み電話番号
 * @returns 数字のみの文字列
 */
export const extractNumbers = (phone: string): string => {
  return phone.replace(/[^\d]/g, '');
};